/**
 * usePredictions Hook - Phase 2C-2
 * 
 * Fetches user's predictions.
 * Uses PredictionService pattern.
 */

import { useState, useEffect, useCallback } from 'react';
import { Prediction, PredictionStatus } from '../types';
import { PredictionService, ApiError } from '../api';

interface UsePredictionsResult {
  predictions: Prediction[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  filterByStatus: (status: PredictionStatus) => Prediction[];
}

export function usePredictions(userId: string, poolId?: string): UsePredictionsResult {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPredictions = useCallback(async () => {
    if (!userId || userId === 'guest') {
      setPredictions([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const data = await PredictionService.getUserPredictions(userId, poolId);
      setPredictions(data);
    } catch (err) {
      // 404 means feature not available yet
      if (err instanceof ApiError && err.status === 404) {
        setPredictions([]);
      } else {
        setError(err instanceof ApiError ? err.message : 'Failed to load predictions');
      }
    } finally {
      setLoading(false);
    }
  }, [userId, poolId]);

  useEffect(() => {
    fetchPredictions();
  }, [fetchPredictions]);

  const filterByStatus = useCallback(
    (status: PredictionStatus) => predictions.filter(p => p.status === status),
    [predictions]
  );

  return { predictions, loading, error, refetch: fetchPredictions, filterByStatus };
}
